import { useState, useEffect, useMemo } from 'react';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { fetchOrders, updateOrderStatus, fetchOrdersHeader, deleteOrder } from '../orders-controller/order-controller';
import { toast } from 'react-toastify';

const useOrdersLogic = () => {
    const [lastApiCall, setLastApiCall] = useState({
        page: 1,
        pageSize: 10,
        status: '',
        search: '',
        startDate: '',
        endDate: '',
        syncro: '',
        platform: '' // Add platform tracking
    });

    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('pending');
    const [openDropdown, setOpenDropdown] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10); // Make sure this state exists
    const [totalItems, setTotalItems] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const [orderToDelete, setOrderToDelete] = useState(null);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [statusSummary, setStatusSummary] = useState({
        allOrders: 0,
        abandoned: 0,
        pending: 0,
        confirmed: 0,
        readyToShip: 0,
        shipped: 0,
        delivered: 0,
        canceled: 0,
        paid: 0,
        return: 0,
        returnReceived: 0,
        dechet: 0,
    });
    const [apiSearchTerm, setApiSearchTerm] = useState('');
    const [apiStatusFilter, setApiStatusFilter] = useState('pending');
    const [currentStatus, setCurrentStatus] = useState('En attente'); // Example initial status

    // Add state for date filters
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [apiStartDate, setApiStartDate] = useState('');
    const [apiEndDate, setApiEndDate] = useState('');
    const [apiSyncroFilter, setApiSyncroFilter] = useState('');
    const [apiPlatformFilter, setApiPlatformFilter] = useState(''); // Add platform filter state

    // Status transition logic
    const statusTransitions = {
        'En attente': ['Confirmé', 'Annulé'],
        'Confirmé': ['Annulé', 'Prêt à expédier', 'Expédié', 'Payé', 'Livré'],
        'Prêt à expédier': ['Expédié', 'Livré', 'Annulé', 'Payé'],
        'Expédié': ['Livré', 'Annulé', 'Payé'],
        'Livré': ['Retour', 'Payé', 'Retour reçu'],
        'Annulé': [], // Can still be paid even if canceled
        'Abandonné': ['Payé'], // Can still be paid if abandoned
        'Payé': [], // Paid orders can still be processed
        'Retour': ['Retour reçu'],
        'Retour reçu': ['Déchet'], // After return received, can be marked as déchet
        'Déchet': [], // Final state - no further transitions
    };

    // Options de statut avec traductions et couleurs
    const statusOptions = [
        { value: 'En attente', label: 'En attente', color: 'bg-amber-100 text-amber-800' },
        { value: 'Prêt à expédier', label: 'Prêt à expédier', color: 'bg-emerald-100 text-emerald-800' },
        { value: 'Expédié', label: 'Expédié', color: 'bg-blue-100 text-blue-800' },
        { value: 'Confirmé', label: 'Confirmé', color: 'bg-green-100 text-green-800' },
        { value: 'Livré', label: 'Livré', color: 'bg-green-100 text-green-800' },
        { value: 'Annulé', label: 'Annulé', color: 'bg-red-100 text-red-800' },
        { value: 'Abandonné', label: 'Abandonné', color: 'bg-gray-100 text-gray-800' },
        { value: 'Paid', label: 'Payé', color: 'bg-indigo-100 text-indigo-800' },
        { value: 'Retour', label: 'Retour', color: 'bg-orange-100 text-orange-800' },
        { value: 'Retour reçu', label: 'Retour reçu', color: 'bg-purple-100 text-purple-800' },
        { value: 'Déchet', label: 'Déchet', color: 'bg-red-100 text-red-900' },
    ];

    // Function to get allowed status options based on the current status
    const getAllowedStatusOptions = (currentStatus) => {
        const allowedStatuses = statusTransitions[currentStatus] || [];
        return statusOptions.filter(option => allowedStatuses.includes(option.value));
    };

    // Get allowed status options based on the current status
    const allowedStatusOptions = getAllowedStatusOptions(currentStatus);

    // Get orders from API
    useEffect(() => {
        let isMounted = true;
        let timeoutId = null;

        const getOrders = async () => {
            try {
                if (!isMounted) return;

                setLoading(true);
                console.log('Fetching orders with filters:');
                const response = await fetchOrders(
                    currentPage,
                    pageSize,
                    apiStatusFilter,
                    apiSearchTerm,
                    apiStartDate,
                    apiEndDate,
                    apiSyncroFilter,
                    apiPlatformFilter // Add platform parameter
                );

                if (!isMounted) return;

                // Check if response has the expected structure with a 'list' property
                if (response && response.list) {
                    const ordersList = response.list;
                    setOrders(ordersList);

                    // Set total items and pages from API response
                    setTotalItems(response.total || 0);
                    setTotalPages(response.maxPage || Math.ceil((response.total || 0) / pageSize));

                    // Make sure we don't show "no orders" when we have orders
                    if (ordersList.length === 0) {
                        console.log('No orders found in the response');
                    }
                } else {
                    console.warn('Invalid API response format:', response);
                    throw new Error('Invalid response format');
                }
            } catch (err) {
                if (!isMounted) return;

                console.error('Failed to fetch orders:', err);
                setError('Failed to load orders. Please try again.');
                setOrders([]);  // Set empty array explicitly
            } finally {
                if (isMounted) {
                    setLoading(false);
                }
            }
        };

        // Clear any existing timeout
        if (timeoutId) {
            clearTimeout(timeoutId);
        }

        // Use a small delay to allow for potentially rapid state changes to settle
        timeoutId = setTimeout(() => {
            getOrders();
        }, 100);

        return () => {
            isMounted = false;
            if (timeoutId) {
                clearTimeout(timeoutId);
            }
        };
    }, [currentPage, pageSize, apiStatusFilter, apiSearchTerm, apiStartDate, apiEndDate, apiSyncroFilter, apiPlatformFilter]);


    // Fetch order header data
    useEffect(() => {
        fetchHeaderData();
        fetchStatusSummary(); // Add this line

        // Set up a refresh interval (optional)
        const interval = setInterval(() => {
            fetchHeaderData();
            fetchStatusSummary(); // Add this line
        }, 5 * 60 * 1000); // Refresh every 5 minutes

        return () => clearInterval(interval);
    }, []); // Empty dependency array means this runs once on component mount

    // Calculate status summary
    const calculateStatusSummary = (orderData) => {
        const summary = {
            allOrders: 0,
            abandoned: 0,
            pending: 0,
            confirmed: 0,
            readyToShip: 0,
            shipped: 0,
            delivered: 0,
            canceled: 0,
            paid: 0
        };

        // Loop through orders and count each status
        summary.allOrders = orderData.length;
        orderData.forEach(order => {
            const status = order.etat?.toLowerCase();

            // Increment the appropriate counter using consistent mapping
            if (status === 'annulé' || status === 'canceled') {
                summary.canceled++;
            } else if (status === 'livré' || status === 'delivered') {
                summary.delivered++;
            } else if (status === 'expédié' || status === 'shipped') {
                summary.shipped++;
            } else if (status === 'confirmé' || status === 'confirmed') {
                summary.confirmed++;
            } else if (status === 'en attente' || status === 'pending') {
                summary.pending++;
            } else if (status === 'abandonné' || status === 'abandoned') {
                summary.abandoned++;
            } else if (status === 'prêt à expédier' || status === 'ready_to_ship') {
                summary.readyToShip++;
            } else if (status === 'paid') {
                summary.paid++;
            }

            // Check if the order is paid
            if (order.isPaid) {
                summary.paid++;
            }
        });

        setStatusSummary(summary);
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'Date non disponible';
        const date = new Date(dateString);
        return new Intl.DateTimeFormat('fr-FR', {
            day: 'numeric',
            month: 'long',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        }).format(date);
    };

    // Update order status (would need a proper API endpoint)
    const handleUpdateStatus = async (orderId, newStatus) => {
        try {
            setLoading(true);

            // Map from status values to API keys if needed
            const statusValueToApiKeyMap = {
                'Abandonné': 'abandoned',
                'En attente': 'pending',
                'Confirmé': 'confirmed',
                'Prêt à expédier': 'readyToShip',
                'Expédié': 'shipped',
                'Livré': 'delivered',
                'Annulé': 'canceled',
                'Retour': 'return',
                'Retour reçu': 'returnReceived',
                'Payé': 'paid',
                'Déchet': 'dechet'
            };

            // Get the API key for this status (or use the original if no mapping exists)
            const apiStatusKey = statusValueToApiKeyMap[newStatus] || newStatus;

            const response = await updateOrderStatus(orderId, apiStatusKey);

            // Optimistic update for UI - still use the display value in the UI
            setOrders(prevOrders => {
                const updatedOrders = prevOrders.map(order =>
                    order.id === orderId
                        ? { ...order, etat: newStatus }
                        : order
                );
                return updatedOrders;
            });

            // Update the current status
            setCurrentStatus(newStatus);

            // Refresh header data only after updating an order status
            await fetchHeaderData();

        } catch (error) {
            console.error('Failed to update order status:', error);
            return error;
        } finally {
            setLoading(false);
        }
    };

    // Traduction du statut pour l'affichage
    const translateStatus = (status) => {
        const translations = {
            'delivered': 'Livré',
            'processing': 'En traitement',
            'shipped': 'Expédié',
            'cancelled': 'Annulé',
            'refunded': 'Remboursé',
            'pending': 'En attente',
            'abandoned': 'Abandonné',
            'ready_to_ship': 'Prêt à expédier',
            'return': 'Retour',
            'return_received': 'Retour reçu',
            'paid': 'Payé'
        };
        return translations[status?.toLowerCase()] || status;
    };

    const getClientFullName = (order) => {
        return `${order.clientFirstName || ''} ${order.clientLastName || ''}`.trim() || 'Client non défini';
    };

    const getUserFullName = (order) => {
        if (!order.utilisateur) return 'Utilisateur non défini';
        return `${order.utilisateur.firstName || ''} ${order.utilisateur.lastName || ''}`.trim() || order.utilisateur.userName || 'Utilisateur non défini';
    };

    const getUserEmail = (order) => {
        return order.utilisateur?.email || '';
    };

    // If you're doing client-side filtering, check this
    const filteredOrders = useMemo(() => {
        // Make debugging easier

        return orders;

        // Note: You shouldn't be filtering on the client if the server is already doing it
        // If you are doing additional client filtering, double check the logic here
    }, [orders]);

    // Ensure the currentOrders calculation is correct
    const currentOrders = filteredOrders;

    // Pagination with server-side data
    const paginate = (pageNumber) => {
        // Check if this is a duplicate call with the same parameters
        if (pageNumber === lastApiCall.page) {
            console.log('Ignoring duplicate page change request');
            return;
        }

        setCurrentPage(pageNumber);

        // Update the last API call record
        setLastApiCall(prev => ({
            ...prev,
            page: pageNumber
        }));

        // Only scroll to top for explicit page navigation (not filter changes)
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const getStatusColor = (status) => {
        const statusLower = status?.toLowerCase() || '';

        if (statusLower === 'delivered' || statusLower === 'livré') {
            return 'bg-green-100 text-green-800';
        }
        if (statusLower === 'processing' || statusLower === 'en traitement') {
            return 'bg-yellow-100 text-yellow-800';
        }
        if (statusLower === 'shipped' || statusLower === 'expédié') {
            return 'bg-blue-100 text-blue-800';
        }
        if (statusLower === 'cancelled' || statusLower === 'annulé') {
            return 'bg-red-100 text-red-800';
        }
        if (statusLower === 'refunded' || statusLower === 'remboursé') {
            return 'bg-purple-100 text-purple-800';
        }
        if (statusLower === 'pending' || statusLower === 'en attente') {
            return 'bg-amber-100 text-amber-800';
        }
        if (statusLower === 'abandoned' || statusLower === 'abandonné') {
            return 'bg-gray-100 text-gray-800';
        }
        if (statusLower === 'ready_to_ship' || statusLower === 'prêt à expédier') {
            return 'bg-emerald-100 text-emerald-800';
        }
        if (statusLower === 'return' || statusLower === 'retour') {
            return 'bg-orange-100 text-orange-800';
        }
        if (statusLower === 'return_received' || statusLower === 'retour reçu') {
            return 'bg-purple-100 text-purple-800';
        }
        if (statusLower === 'paid' || statusLower === 'payé') {
            return 'bg-teal-100 text-teal-800';
        }

        return 'bg-gray-100 text-gray-800';
    };

    // Excel export functionality
    // Excel export functionality
    const exportToExcel = () => {
        // Create a worksheet with filtered orders
        const worksheet = XLSX.utils.json_to_sheet(
            filteredOrders.map(order => ({
                'ID Commande': order.id,
                'Code Livraison': order.codelivraison || '-', // Add this new field
                'Référence': order.reference || 'N/A',
                'Client': getClientFullName(order),
                'Utilisateur': getUserFullName(order),
                'Email': getUserEmail(order),
                'Date': formatDate(order.dateBonCommande),
                'Total TTC': `${(order.totalTTC || 0).toFixed(2)} TND`,
                'Total HT': `${(order.totalHt || 0).toFixed(2)} TND`,
                'Total TVA': `${(order.totalTva || 0).toFixed(2)} TND`,
                'Remise': `${(order.totalRemise || 0).toFixed(2)} TND`,
                'Statut': translateStatus(order.etat),
                'Source': order.source || 'N/A',
                'Adresse': order.clientAdresse || 'N/A',
                'Téléphone': order.clientNumber || 'N/A',
                'Ville': order.ville || 'N/A'
            }))
        );

        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Commandes');

        // Generate Excel file and save it
        const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
        const data = new Blob([excelBuffer], { type: 'application/octet-stream' });

        // Generate date string for filename
        const date = new Date();
        const dateString = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;

        // Save file
        saveAs(data, `commandes-export-${dateString}.xlsx`);
    };

    // Dropdown for status change
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (openDropdown && !event.target.closest(`#status-menu-${openDropdown}`)) {
                setOpenDropdown(null);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [openDropdown]);

    const toggleDropdown = (orderId) => {
        setOpenDropdown(openDropdown === orderId ? null : orderId);
    };

    // Delete order (would need a proper API endpoint)
    const handleDeleteOrder = (orderId) => {
        setOrderToDelete(orderId);
        setIsDeleteModalOpen(true);
    };

    const confirmDeleteOrder = async () => {
    try {
        // Call the API to delete the order
        await deleteOrder(orderToDelete);

        // Update local state after successful API call
        setOrders(prevOrders => {
            const updatedOrders = prevOrders.filter(order => order.id !== orderToDelete);
            calculateStatusSummary(updatedOrders);
            return updatedOrders;
        });

                // Refresh header / status summary from server so the header counts reflect the full dataset
                try {
                    await refreshStatusSummary();
                } catch (err) {
                    console.warn('Failed to refresh status summary after delete:', err);
                }

                // Also refresh orders header / totals to keep pagination and totals in sync with server
                try {
                    await fetchHeaderData();
                } catch (err) {
                    console.warn('Failed to fetch header data after delete:', err);
                }

        // Show success toast
        toast.success('Commande supprimée avec succès');

        // Close the modal and reset state
        setIsDeleteModalOpen(false);
        setOrderToDelete(null);

    } catch (error) {
        console.error('Failed to delete order:', error);
        toast.error(`Échec de la suppression de la commande: ${error.message}`);

        setIsDeleteModalOpen(false);
        setOrderToDelete(null);
    }
};

    // For displaying the current range of items
    const indexOfFirstOrder = (currentPage - 1) * pageSize + 1;
    const indexOfLastOrder = Math.min(currentPage * pageSize, totalItems);

    // Add a handler for the status filter change from status summary
    // In OrdersLogic.js - Update handleStatusFilterChange function
    const handleStatusFilterChange = (statusKey) => {
        try {
            // Reset to page 1 when changing filters
            setCurrentPage(1);

            // Convert key to API filter value if needed
            const keyToValueMap = {
                'pending': 'En attente',
                'confirmed': 'Confirmé',
                'readyToShip': 'Prêt à expédier',
                'shipped': 'Expédié',
                'delivered': 'Livré',
                'canceled': 'Annulé',
                'abandoned': 'Abandonné',
                'return': 'Retour',
                'returnReceived': 'Retour reçu',
                'paid': 'Payé',  // Using 'paye' for the French version to distinguish from 'paid'
                'dechet': 'Déchet'
            };

            // If your API expects values (French names), convert keys to values
            const apiValue = statusKey;

            // Set the API status filter
            setApiStatusFilter(apiValue);

            // Also update the UI status filter
            setStatusFilter(statusKey || 'all');

            // DO NOT call fetchHeaderData here!
        } catch (error) {
            console.error('Error changing status filter:', error);
        }
    };

    // Update the applyFilters function to include platform parameter
    const applyFilters = (searchTermValue, statusValue, startDateValue, endDateValue, syncroFilterValue, platformFilterValue) => {
        // Check if these are the exact same filter values as before
        if (
            searchTermValue === lastApiCall.search &&
            statusValue === lastApiCall.status &&
            startDateValue === lastApiCall.startDate &&
            endDateValue === lastApiCall.endDate &&
            syncroFilterValue === lastApiCall.syncro &&
            platformFilterValue === lastApiCall.platform
        ) {
            console.log('Ignoring duplicate filter request');
            return;
        }

        // Set the state values properly
        setApiSearchTerm(searchTermValue || '');
        setApiStatusFilter(statusValue || '');
        setApiStartDate(startDateValue || '');
        setApiEndDate(endDateValue || '');
        setApiSyncroFilter(syncroFilterValue || '');
        setApiPlatformFilter(platformFilterValue || ''); // Add platform filter state
        setCurrentPage(1); // Reset to first page when filters change

        // Update the last API call record
        setLastApiCall({
            page: 1,
            pageSize,
            search: searchTermValue || '',
            status: statusValue || '',
            startDate: startDateValue || '',
            endDate: endDateValue || '',
            syncro: syncroFilterValue || '',
            platform: platformFilterValue || '' // Add platform to tracking
        });
    };

    // Update the fetchHeaderData function to include platform filter
    const fetchHeaderData = async () => {
        try {
            setLoading(true);
            console.log('Fetching orders with current filters:', {
                page: currentPage,
                pageSize,
                status: apiStatusFilter,
                search: apiSearchTerm,
                startDate: apiStartDate,
                endDate: apiEndDate,
                syncro: apiSyncroFilter,
                platform: apiPlatformFilter // Add platform to log
            });

            const data = await fetchOrders(
                currentPage,
                pageSize,
                apiStatusFilter,
                apiSearchTerm,
                apiStartDate,
                apiEndDate,
                apiSyncroFilter,
                apiPlatformFilter // Add platform parameter
            );

            if (data && data.list) {
                setOrders(data.list);
                setTotalItems(data.total || 0);
                setTotalPages(data.maxPage || 1);
            }
        } catch (err) {
            console.error('Error fetching orders:', err);
            setError('Erreur lors du chargement des commandes');
        } finally {
            setLoading(false);
        }
    };

    // Add this function right after the existing fetchHeaderData function
    const fetchStatusSummary = async () => {
        try {
            console.log('Fetching status summary from header API...');
            
            const headerData = await fetchOrdersHeader(); // Call the header API
            console.log('Header data received:', headerData);

            // Map the API response to the statusSummary structure
            setStatusSummary({
                allOrders: headerData.tous || 0,
                abandoned: headerData.pannnierAbondonner || 0,
                pending: headerData.enAttente || 0,
                confirmed: headerData.confirmees || 0,
                readyToShip: headerData.pretAExpedier || 0,
                shipped: headerData.expedier || 0,
                delivered: headerData.livrer || 0,
                canceled: headerData.anuller || 0,
                return: headerData.return || 0,
                returnReceived: headerData.returnReceived || 0,
                paid: headerData.paid || 0,
                dechet: headerData.dechet || 0
            });

            console.log('Status summary updated successfully');

        } catch (error) {
            console.error('Failed to fetch status summary:', error);
        }
    };

    // Add this function to handle page size changes
    const handlePageSizeChange = (newSize) => {
        // Check if this is a duplicate call with the same parameters
        if (newSize === lastApiCall.pageSize) {
            console.log('Ignoring duplicate page size change request');
            return;
        }

        setPageSize(newSize);
        setCurrentPage(1); // Reset to first page when changing page size

        // Update the last API call record
        setLastApiCall(prev => ({
            ...prev,
            page: 1,
            pageSize: newSize
        }));
    };

    // Add these two functions to useOrdersLogic hook
    const refreshOrderData = async () => {
        try {
            setLoading(true);
            const response = await fetchOrders(
                currentPage,
                pageSize,
                apiStatusFilter,
                apiSearchTerm,
                apiStartDate,
                apiEndDate,
                apiSyncroFilter,
                apiPlatformFilter
            );

            if (response && response.list) {
                const ordersList = response.list;
                setOrders(ordersList);

                // Set total items and pages from API response
                setTotalItems(response.total || 0);
                setTotalPages(response.maxPage || Math.ceil((response.total || 0) / pageSize));
            } else {
                console.warn('Invalid API response format:', response);
            }
        } catch (error) {
            console.error('Failed to refresh orders data:', error);
            toast.error("Échec du rafraîchissement des données");
        } finally {
            setLoading(false);
        }
    };

    const refreshStatusSummary = async () => {
        try {
            const headerData = await fetchOrdersHeader();

            // Map API response to statusSummary structure
            setStatusSummary({
                allOrders: headerData.tous || 0,
                abandoned: headerData.pannnierAbondonner || 0,
                pending: headerData.enAttente || 0,
                confirmed: headerData.confirmees || 0,
                readyToShip: headerData.pretAExpedier || 0,
                shipped: headerData.expedier || 0,
                delivered: headerData.livrer || 0,
                canceled: headerData.anuller || 0,
                return: headerData.return || 0,
                returnReceived: headerData.returnReceived || 0,
                paid: headerData.paid || 0,
                dechet: headerData.dechet || 0
            });

        } catch (error) {
            console.error('Failed to refresh status summary:', error);
        }
    };

    return {
        // States
        orders,
        loading,
        error,
        searchTerm,
        setSearchTerm,
        statusFilter,
        setStatusFilter,
        openDropdown,
        setOpenDropdown,
        currentPage,
        pageSize,
        totalItems,
        isDeleteModalOpen,
        setIsDeleteModalOpen,
        orderToDelete,
        statusSummary,
        statusOptions, // Complete set of all status options
        allowedStatusOptions, // Filtered options based on current status
        apiSearchTerm,
        apiStatusFilter,
        currentStatus,
        startDate,
        setStartDate,
        endDate,
        setEndDate,
        apiStartDate,
        apiEndDate,
        apiSyncroFilter,
        setApiSyncroFilter,
        apiPlatformFilter,
        setApiPlatformFilter, // Add this line

        // Calculated values
        filteredOrders,
        currentOrders,
        totalPages,
        indexOfFirstOrder,
        indexOfLastOrder,

        // Functions
        calculateStatusSummary,
        formatDate,
        handleUpdateStatus,
        translateStatus,
        getClientFullName,
        getUserFullName,
        getUserEmail,
        paginate,
        getStatusColor,
        exportToExcel,
        toggleDropdown,
        handleDeleteOrder,
        confirmDeleteOrder,
        handleStatusFilterChange,
        applyFilters,
        fetchHeaderData,
        fetchStatusSummary, // Add this line
        refreshOrderData,
        refreshStatusSummary,
        handlePageSizeChange // Add this line
    };
};

export default useOrdersLogic;